import { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { API, BACKEND, api } from "@/lib/api";
import { PaxOverview } from "@/components/pax/PaxOverview";
import { PaxSettings } from "@/components/pax/PaxSettings";
import { ActorDialog } from "@/components/pax/ActorDialog";
import { PaxLog } from "@/components/pax/PaxLog";
import { ManualReportDialog } from "@/components/pax/ManualReportDialog";

export const CopyBox = ({ label, text, testId }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setDone(true);
    toast.success("Kopiert");
    setTimeout(() => setDone(false), 1500);
  };
  return (
    <div className="space-y-1.5">
      <div className="text-sm font-medium text-slate-700">{label}</div>
      <div className="flex gap-2">
        <pre className="flex-1 min-w-0 overflow-x-auto rounded-md bg-slate-900 text-slate-100 text-xs p-3 font-num" data-testid={testId}>{text}</pre>
        <Button variant="outline" size="sm" onClick={copy} className="shrink-0" data-testid={`${testId}-copy`}>{done ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</Button>
      </div>
    </div>
  );
};

const H = ({ title, hint }) => (
  <div className="mb-4">
    <h2 className="font-display text-xl font-semibold text-slate-900">{title}</h2>
    {hint && <p className="text-sm text-slate-500 mt-1">{hint}</p>}
  </div>
);

export const PaxPanel = ({ c, cid, save, settings, onSettings }) => {
  const [open, setOpen] = useState(null);
  const [key, setKey] = useState(0);
  const [ov, setOv] = useState(null);
  const [manual, setManual] = useState(null);
  const pax = c.pax;
  const link = `${API}/pax/${cid}/form`;

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("innsending");
    if (!sid) return;
    api.get(`/customers/${cid}/pax/submissions/${sid}`).then((r) => setOpen({ actorId: r.data.actor_id, subId: sid })).catch(() => {});
  }, [cid]);

  if (settings) {
    return (
      <div className="space-y-12" data-testid="pax-settings-view">
        <section>
          <H title="Skjemaet" hint="Alt lagres automatisk." />
          <PaxSettings pax={pax} cid={cid} onChange={(p, delay) => save("pax", p, delay)} />
        </section>
        <section>
          <H title="Embed-kode og lenke" hint="Lim inn koden på nettsiden, eller del lenken direkte med rederiene." />
          <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 space-y-4">
            <CopyBox label="Embed-kode (WordPress/Elementor: HTML-widget)" testId="pax-embed-code" text={`<div class="havnekalkulator" data-skjema="${cid}"></div>\n<script src="${BACKEND}/api/embed/loader.js" async></script>`} />
            <CopyBox label="Direkte lenke til skjemaet" testId="pax-direct-link" text={link} />
            <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:underline" data-testid="pax-open-form"><ExternalLink className="w-4 h-4" />Åpne skjemaet i ny fane</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-12" data-testid="pax-panel">
      {!pax.enabled && (
        <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="pax-disabled-banner">
          <Info className="w-4 h-4 mt-0.5 shrink-0" /><span>Skjemaet er ikke aktivt ennå. Slå det på under <button type="button" onClick={onSettings} className="underline font-medium" data-testid="pax-open-settings">Innstillinger</button> når du er klar.</span>
        </div>
      )}
      <section>
        <H title="Oversikt over innsendinger" hint="Klikk på et rederi eller en rute for å se hva som er sendt inn og laste ned PDF." />
        <PaxOverview cid={cid} refreshKey={key} onOpen={(actorId, subId) => setOpen({ actorId, subId })} onManual={{ ready: setOv, open: () => setManual({}) }} />
      </section>
      <section><PaxLog cid={cid} refreshKey={key} /></section>
      <ActorDialog cid={cid} open={open} onClose={() => setOpen(null)} onChanged={() => setKey((k) => k + 1)} onManual={(actorId) => { setOpen(null); setManual({ actorId }); }} />
      <ManualReportDialog cid={cid} open={manual} onClose={() => setManual(null)} onSaved={() => setKey((k) => k + 1)}
        actors={ov?.actors || []} periods={ov?.periods || []} price={pax.price} lateFee={pax.late_fee} />
    </div>
  );
};
